import { useLocale } from '@/i18n/LocaleProvider';
import { englishPath, localizePath, SITE_URL } from '@/i18n/locale';
import { localizedAsset } from '@/i18n/Localize';
import { translationAvailable } from '@/i18n/availability';

import React from 'react';
import { Head } from 'vite-react-ssg';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  noindex?: boolean;
  children?: React.ReactNode;
  productSchema?: {
    name: string;
    description: string;
    price?: string;
    currency?: string;
  };
  articleSchema?: {
    headline: string;
    datePublished: string;
    dateModified?: string;
    author: string;
    keywords?: string;
  };
  faqSchema?: Array<{
    question: string;
    answer: string;
  }>;
  breadcrumbSchema?: Array<{
    name: string;
    url: string;
  }>;
}

const SEO = ({
  title = 'GizmoData | Enterprise Analytics Software Solutions',
  description = 'GizmoData provides performance-driven, secure, and reliable enterprise analytics software solutions for your business.',
  keywords,
  author = 'GizmoData',
  image = '/gizmodata-website-image.png',
  url = 'https://gizmodata.com',
  type = 'website',
  twitterCard = 'summary_large_image',
  noindex = false,
  children,
  productSchema,
  articleSchema,
  faqSchema,
  breadcrumbSchema,
}: SEOProps) => {
  const { locale } = useLocale();
  keywords ??= locale === 'pt-BR'
    ? 'análise de dados, SQL, Hardcharger, GizmoEdge, soluções de dados empresariais, big data'
    : 'data analytics, SQL, Hardcharger, GizmoEdge, enterprise data solutions, big data';
  // Construct the full title
  const fullTitle = title.includes('GizmoData') ? title : `${title} | GizmoData`;

  // Absolute URLs — social/AI scrapers don't reliably resolve site-relative paths. SITE_URL is this
  // build's own site (gizmodata.com or gizmodata.com.br); pages pass English URLs and localizePath maps them.
  const toAbsolute = (path: string) =>
    path.startsWith('http') ? path : `${SITE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
  const canonicalUrl = new URL(localizePath(toAbsolute(url), locale));
  canonicalUrl.search = "";
  canonicalUrl.hash = "";
  const canonical = canonicalUrl.href;
  const basePath = englishPath(canonicalUrl.pathname);
  const hasEquivalent = translationAvailable(basePath);
  const absoluteImage = toAbsolute(localizedAsset(image, locale));

  return (
    <Head>
      {/* Basic metadata */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />

      {/* Per-route canonical (owns canonical site-wide; none is set in index.html) */}
      <link rel="canonical" href={canonical} />
      {hasEquivalent && !noindex && <link rel="alternate" hrefLang="en" href={new URL(localizePath(basePath, 'en'), SITE_URL).href} />}
      {hasEquivalent && !noindex && <link rel="alternate" hrefLang="pt-BR" href={new URL(localizePath(basePath, 'pt-BR'), SITE_URL).href} />}
      {hasEquivalent && !noindex && <link rel="alternate" hrefLang="x-default" href={new URL(localizePath(basePath, 'en'), SITE_URL).href} />}
      <meta property="og:locale" content={locale === 'en' ? 'en_US' : 'pt_BR'} />
      {hasEquivalent && <meta property="og:locale:alternate" content={locale === 'en' ? 'pt_BR' : 'en_US'} />}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:site_name" content="GizmoData" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content="@gizmodata" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "GizmoData",
          "url": "https://gizmodata.com",
          "logo": "https://gizmodata.com/gizmodata-logo.png",
          "description": locale === "pt-BR" ? "Empresa de soluções de software de análise de dados empresarial especializada em mecanismos SQL de alto desempenho e ferramentas de processamento de dados." : "Enterprise analytics software solutions provider specializing in high-performance SQL engines and data processing tools.",
          "foundingDate": "2024",
          "sameAs": [
            "https://twitter.com/gizmodata",
            "https://linkedin.com/company/gizmodata"
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "email": "contact@gizmodata.com"
          },
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "US"
          }
        })}
      </script>

      {/* Product Schema */}
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": productSchema.name,
            "description": productSchema.description,
            "brand": {
              "@type": "Brand",
              "name": "GizmoData"
            },
            "offers": productSchema.price ? {
              "@type": "Offer",
              "price": productSchema.price,
              "priceCurrency": productSchema.currency || "USD"
            } : undefined
          })}
        </script>
      )}

      {/* Article Schema */}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "inLanguage": locale,
            "url": canonical,
            "mainEntityOfPage": { "@type": "WebPage", "@id": canonical },
            "headline": articleSchema.headline,
            "datePublished": articleSchema.datePublished,
            "dateModified": articleSchema.dateModified || articleSchema.datePublished,
            "author": {
              "@type": "Person",
              "name": articleSchema.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "GizmoData",
              "logo": {
                "@type": "ImageObject",
                "url": "https://gizmodata.com/gizmodata-logo.png"
              }
            },
            "image": absoluteImage,
            "description": description,
            "keywords": articleSchema.keywords
          })}
        </script>
      )}

      {/* FAQ Schema */}
      {faqSchema && faqSchema.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqSchema.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      )}

      {/* Breadcrumb Schema */}
      {breadcrumbSchema && breadcrumbSchema.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbSchema.map((item, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": item.name,
              "item": localizePath(item.url, locale)
            }))
          })}
        </script>
      )}
      
      {/* Additional elements passed as children */}
      {children}
    </Head>
  );
};

export default SEO;
